import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = {
  username:'', password:'', full_name:'', email:'', role:'receptionist',
  employee_id:'', company_id:'', is_active:true
};
const ALL_ROLES = ['admin','manager','receptionist','boss_secretary','general_user'];
const MANAGER_ROLES = ['manager','receptionist','boss_secretary','general_user'];
const ROLE_COLORS = {
  admin:'bg-red-100 text-red-700',
  manager:'bg-purple-100 text-purple-700',
  receptionist:'bg-blue-100 text-blue-700',
  boss_secretary: 'bg-orange-100 text-orange-700',
  general_user: 'bg-gray-100 text-gray-600'
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showReset, setShowReset] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [loading, setLoading] = useState(true);
  const [empSearch, setEmpSearch] = useState('');
  const { user: me } = useAuth();

  const isAdmin = me?.role === 'admin';
  const ROLES = isAdmin ? ALL_ROLES : MANAGER_ROLES;

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/api/users'); setUsers(r.data); }
    catch (err) { console.error('users:', err); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch (err) { console.error('companies:', err); setCompanies([]); }
    }
    setLoading(false);
  };

  useEffect(() => { if (me) load(); }, [me?.role]);

  // When employee is selected, auto-fill company
  const handleEmployeeChange = async (empId) => {
    setForm(f => ({ ...f, employee_id: empId }));
    if (!empId) return;
    const emp = employees.find(e => e.id === parseInt(empId));
    if (emp?.company_id) {
      setForm(f => ({ ...f, employee_id: empId, company_id: String(emp.company_id) }));
    }
    // Check if employee already has a login
    try {
      const res = await api.get(`/api/users/employee/${empId}/check`);
      if (res.data.hasLogin && res.data.user?.id !== editing) {
        alert(`⚠️ This employee already has a login: @${res.data.user.username}`);
        setForm(f => ({ ...f, employee_id: '' }));
      }
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.employee_id === '') payload.employee_id = null;
    if (payload.company_id === '') payload.company_id = null;
    try {
      if (editing) await api.put(`/api/users/${editing}`, payload);
      else await api.post('/api/users', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null); setEmpSearch('');
      load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleToggle = async (user) => {
    try {
      await api.put(`/api/users/${user.id}`, {
        ...user, is_active: !user.is_active,
        employee_id: user.employee_id || null,
        company_id: user.company_id || null
      });
      load();
    } catch { alert('Update failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('user account', name)) return;
    try { await api.delete(`/api/users/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  const handleResetPass = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/users/${showReset}/reset-password`, { new_password: newPass });
      setShowReset(null); setNewPass('');
      alert('Password reset successfully');
    } catch { alert('Reset failed'); }
  };

  const handleEdit = (u) => {
    setForm({
      username: u.username, password: '', full_name: u.full_name,
      email: u.email || '', role: u.role, is_active: u.is_active,
      employee_id: u.employee_id ? String(u.employee_id) : '',
      company_id: u.company_id ? String(u.company_id) : ''
    });
    setEditing(u.id); setShowForm(true);
  };

  // Search by name/code. When company selected (no search), show that company + unassigned employees.
  // Limit 150 — user must type to find from 400+ employees.
  const filteredEmployees = employees.filter(e => {
    if (empSearch) {
      return e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
        (e.employee_code || '').toLowerCase().includes(empSearch.toLowerCase());
    }
    if (form.company_id) {
      return String(e.company_id) === String(form.company_id) || !e.company_id;
    }
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-500 text-sm">{users.length} users total</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setEmpSearch(''); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Create User
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editing ? 'Edit User' : 'Create User'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); setEmpSearch(''); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-2 gap-4">
              {/* Login Credentials */}
              <div className="col-span-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Login Credentials</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input type="text" value={form.username}
                  onChange={e => setForm({...form,username:e.target.value})}
                  required disabled={!!editing}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
              </div>
              {!editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" value={form.password}
                    onChange={e => setForm({...form,password:e.target.value})}
                    required={!editing} minLength={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div className={editing ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" value={form.full_name}
                  onChange={e => setForm({...form,full_name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email}
                  onChange={e => setForm({...form,email:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={form.role} onChange={e => setForm({...form,role:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {ROLES.map(r => (
                    <option key={r} value={r}>
                      {r === 'admin' ? 'Administrator' :
                       r === 'manager' ? 'Manager' :
                       r === 'receptionist' ? 'Receptionist' :
                       r === 'boss_secretary' ? 'Boss Secretary' :
                       r === 'general_user' ? 'General User' : r}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee Linkage */}
              <div className="col-span-2 border-t pt-4 mt-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Employee Linkage (Optional)</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Employee</label>
                <input type="text" placeholder="Search employee name or code..."
                  value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2" />
                <select value={form.employee_id}
                  onChange={e => handleEmployeeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— No linked employee —</option>
                  {(empSearch ? filteredEmployees : filteredEmployees.slice(0, 500)).map(e => (
                    <option key={e.id} value={e.id}>
                      {e.name} {e.employee_code ? `(${e.employee_code})` : ''} — {e.department || 'No dept'}
                    </option>
                  ))}
                  {!empSearch && filteredEmployees.length > 500 && (
                    <option disabled>— Type above to search all {filteredEmployees.length} employees —</option>
                  )}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Showing {empSearch ? filteredEmployees.length : Math.min(filteredEmployees.length, 500)} of {filteredEmployees.length} employees
                  {!empSearch && filteredEmployees.length > 500 && ' — type above to search all'}
                </p>
                {form.employee_id && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Linked: {employees.find(e => e.id === parseInt(form.employee_id))?.name}
                  </p>
                )}
              </div>
              {isAdmin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Assignment</label>
                  <select value={form.company_id}
                    onChange={e => setForm({...form,company_id:e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— All Companies (Super Admin) —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Auto-filled when employee is linked</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input type="text" disabled value={me?.company_name || 'Your Company'}
                    className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                  <p className="text-xs text-gray-400 mt-1">Auto-assigned to your company</p>
                </div>
              )}
              {editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.is_active ? 'active' : 'inactive'}
                    onChange={e => setForm({...form, is_active: e.target.value === 'active'})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="active">Active</option>
                    <option value="inactive">Disabled</option>
                  </select>
                </div>
              )}

              <div className="col-span-2 flex gap-3 pt-2 border-t">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Create User'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setEmpSearch(''); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-xl font-bold mb-4">Reset Password</h2>
            <form onSubmit={handleResetPass} className="space-y-4">
              <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
                required placeholder="New password" minLength={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <div className="flex gap-3">
                <button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium">Reset</button>
                <button type="button" onClick={() => { setShowReset(null); setNewPass(''); }}
                  className="border px-4 py-2 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Grid */}
      {loading ? (
        <div className="p-12 text-center text-gray-400">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(u => (
            <div key={u.id} className={`bg-white rounded-xl border p-5 ${!u.is_active ? 'opacity-60 border-gray-100' : 'border-gray-100 hover:shadow-md'} transition-shadow`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white ${
                    u.role==='admin' ? 'bg-red-500' : u.role==='manager' ? 'bg-purple-500' : 'bg-blue-500'
                  }`}>{u.full_name.charAt(0)}</div>
                  <div>
                    <p className="font-semibold text-gray-800">{u.full_name}</p>
                    <p className="text-gray-400 text-xs">@{u.username}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm text-gray-500">
                {u.email && <p>✉️ {u.email}</p>}
                {u.employee_name && (
                  <p className="text-blue-600">👤 {u.employee_name}
                    {u.department ? ` · ${u.department}` : ''}
                  </p>
                )}
                {u.company_name && <p>🏢 {u.company_name}</p>}
                <p>🕐 {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never logged in'}</p>
                <p className={`font-medium ${u.is_active ? 'text-green-600' : 'text-red-500'}`}>
                  {u.is_active ? '✅ Active' : '❌ Disabled'}
                </p>
              </div>
              {/* Action buttons — always shown except self-disable/delete */}
              {(u.id !== me?.id || isAdmin) && !(u.role === 'admin' && !isAdmin) ? (
                <div className="mt-4 flex gap-2 border-t border-gray-50 pt-3">
                  <button onClick={() => handleEdit(u)} className="flex-1 text-xs py-1.5 border rounded hover:bg-gray-50">✏️ Edit</button>
                  {/* Reset: always allowed for self; for others — manager cannot reset admin */}
                  {(u.id === me?.id || isAdmin || u.role !== 'admin') && (
                    <button onClick={() => setShowReset(u.id)} className="flex-1 text-xs py-1.5 border rounded hover:bg-gray-50">🔑 Reset</button>
                  )}
                  {/* Disable/Enable — not for self */}
                  {u.id !== me?.id && (
                    <button onClick={() => handleToggle(u)} className={`flex-1 text-xs py-1.5 border rounded ${u.is_active ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'}`}>
                      {u.is_active ? '🚫 Disable' : '✅ Enable'}
                    </button>
                  )}
                  {/* Delete — admin only, not self */}
                  {isAdmin && u.id !== me?.id && (
                    <button onClick={() => handleDelete(u.id, u.full_name)} className="text-xs py-1.5 px-2 border rounded hover:bg-red-50 hover:text-red-600">🗑️</button>
                  )}
                </div>
              ) : u.id === me?.id ? (
                /* Own card — only reset password, no role change or disable */
                <div className="mt-4 flex gap-2 border-t border-gray-50 pt-3">
                  <button onClick={() => setShowReset(u.id)} className="flex-1 text-xs py-1.5 border rounded hover:bg-gray-50">🔑 Reset My Password</button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
